import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { PermissionGate, useRBAC } from '@/contexts/RBACContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Shield, User, Check, X, Trash2, 
  Mail, Search, Clock, CheckCircle, XCircle, Edit, MoreVertical, Key, Eye, EyeOff,
  Ban, UserCheck, History, Activity, RefreshCw, Settings, Lock, Download, FileSpreadsheet
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Display labels for role chips/badges. Keep in sync with rbac_roles
// in the database — any role_code not listed here falls back to the
// raw string (e.g. "bdm" instead of "BDM"). The role dropdown itself
// is populated dynamically from /admin/rbac-roles, so a missing
// label here only affects how an existing user's role is displayed,
// not whether you can assign that role.
const roleLabels = {
  super_admin: 'Super Admin',
  account_owner: 'Account Owner',
  owner: 'Owner',
  admin: 'Admin',
  ceo: 'CEO',
  hod_sales: 'HOD Sales',
  hod_marketing: 'HOD Marketing',
  sales_manager: 'Sales Manager',
  sales_agent: 'Sales Agent',
  sales_rep: 'Sales Agent',
  marketing: 'Marketing User',
  marketing_agent: 'Marketing User',
  bdm: 'BDM',
  cr_agent: 'CR Agent',
  ai_agent: 'AI Agent',
  viewer: 'Viewer',
  pending: 'Pending'
};

const roleBadgeColors = {
  super_admin: 'bg-red-500',
  account_owner: 'bg-amber-500',
  owner: 'bg-amber-500',
  admin: 'bg-purple-500',
  ceo: 'bg-amber-600',
  hod_sales: 'bg-blue-600',
  hod_marketing: 'bg-violet-600',
  sales_manager: 'bg-blue-500',
  sales_agent: 'bg-green-500',
  sales_rep: 'bg-green-500',
  marketing: 'bg-violet-500',
  marketing_agent: 'bg-violet-500',
  bdm: 'bg-orange-500',
  cr_agent: 'bg-teal-500',
  ai_agent: 'bg-pink-500',
  viewer: 'bg-slate-500',
  pending: 'bg-yellow-500'
};

const activityTypeLabels = {
  login: 'User Login',
  logout: 'User Logout',
  user_created: 'User Created',
  user_approved: 'User Approved',
  user_rejected: 'User Rejected',
  user_disabled: 'User Disabled',
  user_enabled: 'User Enabled',
  user_deleted: 'User Deleted',
  role_changed: 'Role Changed',
  password_reset: 'Password Reset',
  password_changed: 'Password Changed',
  contact_created: 'Contact Created',
  contact_updated: 'Contact Updated',
  contact_deleted: 'Contact Deleted',
  settings_updated: 'Settings Updated'
};

const activityTypeColors = {
  login: 'bg-green-100 text-green-700',
  logout: 'bg-slate-100 text-slate-700',
  user_created: 'bg-blue-100 text-blue-700',
  user_approved: 'bg-green-100 text-green-700',
  user_rejected: 'bg-red-100 text-red-700',
  user_disabled: 'bg-orange-100 text-orange-700',
  user_enabled: 'bg-green-100 text-green-700',
  user_deleted: 'bg-red-100 text-red-700',
  role_changed: 'bg-purple-100 text-purple-700',
  password_reset: 'bg-amber-100 text-amber-700',
  password_changed: 'bg-amber-100 text-amber-700',
  contact_created: 'bg-blue-100 text-blue-700',
  contact_updated: 'bg-blue-100 text-blue-700',
  contact_deleted: 'bg-red-100 text-red-700',
  settings_updated: 'bg-slate-100 text-slate-700'
};

const UserManagement = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  
  // Dialog states
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showRoleAssignDialog, setShowRoleAssignDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState({});
  
  // RBAC Role assignment state
  const [rbacRoles, setRbacRoles] = useState([]);
  const [userRbacRoles, setUserRbacRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [savingRoles, setSavingRoles] = useState(false);
  
  // Organizations state
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  
  // Available roles state
  const [availableRoles, setAvailableRoles] = useState([]);
  
  // Reset password state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Effective Permissions dialog state
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [effectivePermissions, setEffectivePermissions] = useState({});
  const [userRolesForViewer, setUserRolesForViewer] = useState([]);
  
  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  
  // Bulk selection state
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Form state for new user
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'sales_rep',
    organization_id: ''
  });
  const [addingUser, setAddingUser] = useState(false);
  
  // Edit user state
  const [showEditUserFormDialog, setShowEditUserFormDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    full_name: '',
    role: '',
    organization_id: '',
    phone: '',
    hubspot_user_id: '',
    hubspot_user_email: '',
    hubspot_sync_enabled: true
  });
  const [savingUser, setSavingUser] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchPendingUsers();
    fetchOrganizations();
    fetchAvailableRoles();
  }, []);

  useEffect(() => {
    if (selectedTab === 'audit') {
      fetchAuditLogs();
    }
  }, [selectedTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/accounts/users?token=${token}`);
      if (response.data.success) {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setLoadingLogs(true);
      const response = await axios.get(`${API}/accounts/audit-logs?token=${token}&limit=100`);
      if (response.data.success) {
        setAuditLogs(response.data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API}/accounts/pending-users?token=${token}`);
      if (response.data.success) {
        setPendingUsers(response.data.pending_users || []);
      }
    } catch (error) {
      console.error('Failed to fetch pending users:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const response = await axios.get(`${API}/organizations?token=${token}`);
      if (response.data.success) {
        setOrganizations(response.data.organizations || []);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      const response = await axios.get(`${API}/admin/rbac-roles?token=${token}`);
      if (response.data.success) {
        // Filter out system roles that shouldn't be assignable
        const roles = (response.data.roles || []).filter(r => 
          r.role_code !== 'super_admin' && r.is_active !== false
        );
        setAvailableRoles(roles);
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      // Fallback to default roles
      setAvailableRoles([
        { id: 'admin', role_code: 'admin', role_name: 'Admin' },
        { id: 'sales_manager', role_code: 'sales_manager', role_name: 'Sales Manager' },
        { id: 'sales_rep', role_code: 'sales_rep', role_name: 'Sales Agent' },
        { id: 'viewer', role_code: 'viewer', role_name: 'Viewer' }
      ]);
    }
  };

  // Get organization name by ID or from user object
  const getOrganizationName = (orgIdOrUser) => {
    // If the user object has organization_name already, use it
    if (typeof orgIdOrUser === 'object' && orgIdOrUser?.organization_name) {
      return orgIdOrUser.organization_name;
    }
    // Otherwise look it up from the organizations list
    const orgId = typeof orgIdOrUser === 'string' ? orgIdOrUser : orgIdOrUser?.organization_id;
    if (!orgId) return '-';
    const org = organizations.find(o => o.id === orgId);
    return org ? org.name : '-';
  };

  const handleApproveUser = async (userId) => {
    const role = selectedRoles[userId] || 'sales_rep';
    try {
      const response = await axios.post(`${API}/accounts/users/${userId}/approve?token=${token}&role=${role}`);
      if (response.data.success) {
        toast.success(`User approved as ${roleLabels[role]}`);
        setPendingUsers(prev => prev.filter(u => u.id !== userId));
        fetchUsers(); // Refresh user list
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      const response = await axios.post(`${API}/accounts/users/${userId}/reject?token=${token}`);
      if (response.data.success) {
        toast.success('User access request rejected');
        setPendingUsers(prev => prev.filter(u => u.id !== userId));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddingUser(true);
    try {
      const response = await axios.post(`${API}/accounts/users?token=${token}`, newUser);
      if (response.data.success) {
        toast.success('User added successfully');
        setShowAddUserDialog(false);
        setNewUser({ email: '', full_name: '', password: '', role: 'sales_rep', organization_id: '' });
        fetchUsers();
      }
    } catch (error) {
      // The backend uses two shapes for validation errors — legacy
      // routes return `{detail: "..."}` (FastAPI HTTPException default),
      // while the newer standardized envelope returns `{success: false,
      // error: "...", error_code: "..."}`. Check both so a duplicate-
      // email response surfaces "Email already registered" instead of
      // the generic fallback.
      const err = error.response?.data;
      toast.error(err?.detail || err?.error || err?.message || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  // Open edit user dialog
  const openEditUserForm = (userItem) => {
    setEditingUser(userItem);
    setEditUserForm({
      full_name: userItem.full_name || '',
      role: userItem.role || 'sales_rep',
      organization_id: userItem.organization_id || '',
      phone: userItem.phone || '',
      hubspot_user_id: userItem.hubspot_user_id || '',
      hubspot_user_email: userItem.hubspot_user_email || '',
      hubspot_sync_enabled: userItem.hubspot_sync_enabled !== false
    });
    setShowEditUserFormDialog(true);
  };

  // Handle edit user save
  const handleSaveEditUser = async () => {
    if (!editingUser) return;
    setSavingUser(true);
    
    try {
      // Update basic user info
      const response = await axios.put(`${API}/users/${editingUser.id}?token=${token}`, {
        full_name: editUserForm.full_name,
        role: editUserForm.role,
        organization_id: editUserForm.organization_id || null,
        phone: editUserForm.phone || null
      });
      
      // Update HubSpot mapping separately
      if (editUserForm.hubspot_user_id !== (editingUser.hubspot_user_id || '') ||
          editUserForm.hubspot_user_email !== (editingUser.hubspot_user_email || '') ||
          editUserForm.hubspot_sync_enabled !== (editingUser.hubspot_sync_enabled !== false)) {
        try {
          await axios.put(`${API}/accounts/users/${editingUser.id}/hubspot-mapping?token=${token}`, {
            hubspot_user_id: editUserForm.hubspot_user_id || null,
            hubspot_user_email: editUserForm.hubspot_user_email || null,
            hubspot_sync_enabled: editUserForm.hubspot_sync_enabled
          });
        } catch (hubspotError) {
          console.error('Failed to update HubSpot mapping:', hubspotError);
          toast.error('User saved but HubSpot mapping failed');
        }
      }
      
      if (response.data.success) {
        toast.success('User updated successfully');
        setShowEditUserFormDialog(false);
        setEditingUser(null);
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const response = await axios.put(`${API}/accounts/users/${userId}/role?token=${token}`, {
        role: newRole
      });
      if (response.data.success) {
        toast.success('User role updated');
        setShowEditUserDialog(false);
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user role');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const response = await axios.delete(`${API}/accounts/users/${userId}?token=${token}`);
      if (response.data.success) {
        toast.success('User deleted successfully');
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      const response = await axios.put(`${API}/users/${selectedUser?.id}/reset-password?token=${token}`, {
        password: newPassword
      });
      if (response.data.success) {
        toast.success('Password reset successfully');
        setShowResetPasswordDialog(false);
        setNewPassword('');
        setConfirmPassword('');
        setSelectedUser(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const openResetPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowResetPasswordDialog(true);
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    const action = currentStatus ? 'enable' : 'disable';
    const confirmMsg = currentStatus 
      ? 'Are you sure you want to enable this user? They will be able to login again.'
      : 'Are you sure you want to disable this user? They will not be able to login.';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const response = await axios.post(`${API}/accounts/users/${userId}/${action}?token=${token}`);
      if (response.data.success) {
        toast.success(`User ${action}d successfully`);
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const openPermissionsDialog = async (userItem) => {
    setSelectedUser(userItem);
    setLoadingPermissions(true);
    setShowPermissionsDialog(true);
    
    try {
      // Fetch user's effective permissions and roles
      const [permsRes, rolesRes] = await Promise.all([
        axios.get(`${API}/users/${userItem.id}/permissions/effective?token=${token}`),
        axios.get(`${API}/users/${userItem.id}/rbac-roles?token=${token}`)
      ]);
      
      if (permsRes.data.success) {
        setEffectivePermissions(permsRes.data.permissions || {});
      }
      if (rolesRes.data.success) {
        setUserRolesForViewer(rolesRes.data.roles || []);
      }
    } catch (error) {
      console.error('Failed to fetch effective permissions:', error);
      toast.error('Failed to load permissions');
      setEffectivePermissions({});
      setUserRolesForViewer([]);
    } finally {
      setLoadingPermissions(false);
    }
  };

  // Group permissions by menu (first part of permission code)
  const groupedPermissions = Object.entries(effectivePermissions).reduce((acc, [code, actions]) => {
    const parts = code.split('.');
    const menu = parts[0] || 'OTHER';
    const subMenu = parts.slice(1).join('.') || code;
    
    if (!acc[menu]) {
      acc[menu] = [];
    }
    acc[menu].push({ code, subMenu, actions: Array.isArray(actions) ? actions : [] });
    return acc;
  }, {});

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Dubai'
    });
  };

  // RBAC Role Management Functions
  const fetchRbacRoles = async () => {
    try {
      const response = await axios.get(`${API}/admin/rbac-roles?token=${token}`);
      if (response.data.success) {
        // Filter out system roles that shouldn't be manually assigned
        const assignableRoles = (response.data.roles || []).filter(r => 
          !['super_admin', 'pending'].includes(r.role_code)
        );
        setRbacRoles(assignableRoles);
      }
    } catch (error) {
      console.error('Failed to fetch RBAC roles:', error);
    }
  };

  const fetchUserRbacRoles = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/rbac-roles?token=${token}`);
      if (response.data.success) {
        setUserRbacRoles(response.data.roles || []);
      }
    } catch (error) {
      console.error('Failed to fetch user roles:', error);
      setUserRbacRoles([]);
    }
  };

  const openRoleAssignDialog = async (userItem) => {
    setSelectedUser(userItem);
    setLoadingRoles(true);
    setShowRoleAssignDialog(true);
    
    // Fetch both available roles and user's current roles
    await Promise.all([
      fetchRbacRoles(),
      fetchUserRbacRoles(userItem.id)
    ]);
    setLoadingRoles(false);
  };

  const handleToggleUserRole = (roleId) => {
    setUserRbacRoles(prev => {
      const hasRole = prev.some(r => r.id === roleId);
      if (hasRole) {
        return prev.filter(r => r.id !== roleId);
      } else {
        const roleToAdd = rbacRoles.find(r => r.id === roleId);
        return roleToAdd ? [...prev, roleToAdd] : prev;
      }
    });
  };

  const handleSaveUserRoles = async () => {
    if (!selectedUser) return;
    setSavingRoles(true);
    
    try {
      const roleIds = userRbacRoles.map(r => r.id);
      const response = await axios.put(
        `${API}/users/${selectedUser.id}/rbac-roles?token=${token}`,
        { role_ids: roleIds }
      );
      
      if (response.data.success) {
        toast.success('User roles updated successfully');
        setShowRoleAssignDialog(false);
        fetchUsers(); // Refresh to show updated role info
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user roles');
    } finally {
      setSavingRoles(false);
    }
  };

  // Bulk selection handlers
  const handleSelectUser = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      }
      return [...prev, userId];
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // Select all users except current user
      const selectableUsers = users.filter(u => u.id !== user?.id).map(u => u.id);
      setSelectedUserIds(selectableUsers);
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedUserIds.length} selected user(s)? This action cannot be undone.`)) {
      return;
    }
    
    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const userId of selectedUserIds) {
      try {
        const response = await axios.delete(`${API}/accounts/users/${userId}?token=${token}`);
        if (response.data.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }
    
    setBulkDeleting(false);
    setSelectedUserIds([]);
    
    if (successCount > 0) {
      toast.success(`Successfully deleted ${successCount} user(s)`);
      fetchUsers();
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} user(s)`);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">User Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage users, approve access requests, and assign roles
          </p>
        </div>
        
        <PermissionGate permission="ADMINISTRATION.USER_MANAGEMENT" action="create">
          <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600 gap-2" data-testid="add-user-button">
                <UserPlus size={16} />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg p-0 overflow-hidden">
              <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-violet-50 to-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                    <UserPlus size={20} />
                  </div>
                  <div className="flex-1">
                    <DialogTitle className="text-lg font-semibold text-slate-900">Add New User</DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 mt-0.5">
                      Create a new user account and assign an initial role.
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <form onSubmit={handleAddUser}>
                <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
                  <div className="space-y-1.5">
                    <Label htmlFor="user-name" className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Full Name
                    </Label>
                    <Input
                      id="user-name"
                      value={newUser.full_name}
                      onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                      placeholder="John Doe"
                      required
                      className="h-10"
                      data-testid="new-user-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="user-email" className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Email
                    </Label>
                    <Input
                      id="user-email"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      placeholder="john@company.com"
                      required
                      className="h-10"
                      data-testid="new-user-email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="user-password" className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Password
                    </Label>
                    <Input
                      id="user-password"
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      className="h-10"
                      data-testid="new-user-password"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="user-role" className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Role
                    </Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger className="h-10" data-testid="new-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRoles.length > 0 ? (
                          availableRoles.map((role) => (
                            <SelectItem key={role.id || role.role_code} value={role.role_code}>
                              {role.role_name}
                            </SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="sales_manager">Sales Manager</SelectItem>
                            <SelectItem value="sales_rep">Sales Agent</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="user-organization" className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Organization
                    </Label>
                    {user?.role === 'super_admin' ? (
                      <Select
                        value={newUser.organization_id || 'none'}
                        onValueChange={(v) => setNewUser({ ...newUser, organization_id: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger className="h-10" data-testid="new-user-organization">
                          <SelectValue placeholder="Select organization" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Organization</SelectItem>
                          {organizations.map((org) => (
                            <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="user-organization"
                        value={organizations.find(o => o.id === user?.organization_id)?.name || 'Your Organization'}
                        disabled
                        className="h-10 bg-slate-100"
                        data-testid="new-user-organization"
                      />
                    )}
                  </div>
                </div>
                <DialogFooter className="px-6 py-4 border-t bg-slate-50/70 gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowAddUserDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={addingUser}
                    className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
                  >
                    {addingUser ? (
                      <><RefreshCw size={14} className="mr-2 animate-spin" /> Adding…</>
                    ) : (
                      <><UserPlus size={14} className="mr-2" /> Add User</>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </PermissionGate>
      </div>

      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList>
          <TabsTrigger value="all" className="gap-2" data-testid="tab-all-users">
            <Users size={16} />
            All Users ({users.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-2" data-testid="tab-pending-users">
            <Clock size={16} />
            Pending Requests ({pendingUsers.length})
            {pendingUsers.length > 0 && (
              <span className="ml-1 w-2 h-2 rounded-full bg-red-500"></span>
            )}
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-2" data-testid="tab-audit-logs">
            <History size={16} />
            Audit Log
          </TabsTrigger>
        </TabsList>

        {/* All Users Tab */}
        <TabsContent value="all" className="mt-4">
          {/* Search */}
          <div className="relative mb-4">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="user-search"
            />
          </div>

          {/* Bulk Actions Bar */}
          {selectedUserIds.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
              <span className="text-blue-700 font-medium">
                {selectedUserIds.length} user(s) selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedUserIds([])}
                  className="text-slate-600"
                >
                  Clear Selection
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="bg-red-500 hover:bg-red-600"
                  data-testid="bulk-delete-btn"
                >
                  {bulkDeleting ? 'Deleting...' : `Delete ${selectedUserIds.length} User(s)`}
                </Button>
              </div>
            </div>
          )}

          {/* Users Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.length > 0 && selectedUserIds.length === users.filter(u => u.id !== user?.id).length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead className="font-semibold">USER</TableHead>
                  <TableHead className="font-semibold">EMAIL</TableHead>
                  <TableHead className="font-semibold">ORGANIZATION</TableHead>
                  <TableHead className="font-semibold">ROLE</TableHead>
                  <TableHead className="font-semibold">STATUS</TableHead>
                  <TableHead className="font-semibold text-right">ACTIONS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      Loading users...
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((u) => (
                    <TableRow key={u.id} className={`${u.is_disabled ? 'opacity-60 bg-slate-50' : ''} ${selectedUserIds.includes(u.id) ? 'bg-blue-50' : ''}`} data-testid={`user-row-${u.id}`}>
                      <TableCell>
                        {u.id !== user?.id && (
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(u.id)}
                            onChange={() => handleSelectUser(u.id)}
                            className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                            data-testid={`select-user-${u.id}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold ${u.is_disabled ? 'bg-slate-200 text-slate-500' : 'bg-amber-100 text-amber-700'}`}>
                            {u.full_name?.[0]?.toUpperCase() || u.email?.[0]?.toUpperCase() || 'U'}
                          </div>
                          <span className={`font-medium ${u.is_disabled ? 'text-slate-500' : 'text-slate-800'}`}>{u.full_name || 'Unknown'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{u.email}</TableCell>
                      <TableCell className="text-slate-600">{getOrganizationName(u)}</TableCell>
                      <TableCell>
                        <Badge className={`${roleBadgeColors[u.role] || 'bg-slate-500'} text-white`}>
                          {roleLabels[u.role] || u.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {u.is_disabled ? (
                          <Badge className="bg-red-100 text-red-700">Disabled</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditUserForm(u)}
                            className="h-8 w-8 p-0"
                            title="Edit User"
                            data-testid={`edit-user-form-${u.id}`}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openResetPasswordDialog(u)}
                            className="h-8 w-8 p-0"
                            title="Reset Password"
                            data-testid={`reset-password-${u.id}`}
                          >
                            <Key size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPermissionsDialog(u)}
                            className="h-8 w-8 p-0 text-purple-500 hover:text-purple-600 hover:bg-purple-50"
                            title="View Permissions"
                            data-testid={`permissions-user-${u.id}`}
                          >
                            <Shield size={14} />
                          </Button>
                          {/* Super admins can delete any user except themselves */}
                          {u.id !== user?.id && (user?.role === 'super_admin' || (u.role !== 'owner' && u.role !== 'account_owner' && u.role !== 'super_admin')) && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleUserStatus(u.id, u.is_disabled)}
                                className={`h-8 w-8 p-0 ${u.is_disabled ? 'text-green-500 hover:text-green-600 hover:bg-green-50' : 'text-orange-500 hover:text-orange-600 hover:bg-orange-50'}`}
                                title={u.is_disabled ? 'Enable User' : 'Disable User'}
                                data-testid={`toggle-user-${u.id}`}
                              >
                                {u.is_disabled ? <UserCheck size={14} /> : <Ban size={14} />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUser(u.id)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                title="Delete User"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Pending Requests Tab */}
        <TabsContent value="pending" className="mt-4">
          {pendingUsers.length === 0 ? (
            <Card className="p-8 text-center">
              <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-medium text-slate-800">No Pending Requests</h3>
              <p className="text-slate-500 mt-1">All user access requests have been processed</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pendingUsers.map((pendingUser) => (
                <Card key={pendingUser.id} className="p-4" data-testid={`pending-user-card-${pendingUser.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-yellow-100 text-yellow-700 flex items-center justify-center text-lg font-semibold">
                        {pendingUser.full_name?.[0]?.toUpperCase() || pendingUser.email?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <h3 className="font-medium text-slate-800">{pendingUser.full_name || 'Unknown User'}</h3>
                        <p className="text-sm text-slate-500">{pendingUser.email}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          <Clock size={12} className="inline mr-1" />
                          Requested: {formatDate(pendingUser.created_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Select
                        value={selectedRoles[pendingUser.id] || 'sales_rep'}
                        onValueChange={(v) => setSelectedRoles(prev => ({...prev, [pendingUser.id]: v}))}
                      >
                        <SelectTrigger className="w-40" data-testid={`role-select-${pendingUser.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="sales_manager">Sales Manager</SelectItem>
                          <SelectItem value="sales_rep">Sales Agent</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Button
                        onClick={() => handleApproveUser(pendingUser.id)}
                        className="bg-green-600 hover:bg-green-700 gap-1"
                        data-testid={`approve-btn-${pendingUser.id}`}
                      >
                        <Check size={16} />
                        Approve
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={() => handleRejectUser(pendingUser.id)}
                        className="text-red-600 border-red-200 hover:bg-red-50 gap-1"
                        data-testid={`reject-btn-${pendingUser.id}`}
                      >
                        <X size={16} />
                        Reject
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit" className="mt-4">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between pb-2 gap-3">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity size={18} />
                  Activity Log
                </CardTitle>
                <CardDescription>Track all user and system activities</CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => window.open(`${API}/accounts/audit-logs/export?token=${token}&format=csv`, '_blank')}
                  className="gap-2"
                  data-testid="export-csv-btn"
                >
                  <Download size={14} />
                  CSV
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => window.open(`${API}/accounts/audit-logs/export?token=${token}&format=excel`, '_blank')}
                  className="gap-2"
                  data-testid="export-excel-btn"
                >
                  <FileSpreadsheet size={14} />
                  Excel
                </Button>
                <Button variant="outline" size="sm" onClick={fetchAuditLogs} className="gap-2">
                  <RefreshCw size={14} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingLogs ? (
                <div className="text-center py-8 text-slate-500">Loading audit logs...</div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <History size={48} className="mx-auto mb-4 opacity-30" />
                  <p>No activity logs found</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {auditLogs.map((log, index) => (
                    <div key={log.id || index} className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 border border-slate-100">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${activityTypeColors[log.activity_type] || 'bg-slate-100 text-slate-700'}`}>
                        <Activity size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={activityTypeColors[log.activity_type] || 'bg-slate-100 text-slate-700'}>
                            {activityTypeLabels[log.activity_type] || log.activity_type}
                          </Badge>
                          <span className="text-xs text-slate-400">
                            {formatDate(log.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 mt-1">{log.description}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                          {log.performed_by_name && (
                            <span>By: <strong>{log.performed_by_name}</strong></span>
                          )}
                          {log.target_user_name && (
                            <span>User: <strong>{log.target_user_name}</strong></span>
                          )}
                          {log.ip_address && (
                            <span>IP: {log.ip_address}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit User Role Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-violet-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                <Shield size={20} />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-lg font-semibold text-slate-900">Edit User Role</DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-0.5">
                  Change the role for {selectedUser?.full_name || selectedUser?.email}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="px-6 py-5 space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Select Role
            </Label>
            <Select
              value={selectedUser?.role || 'sales_rep'}
              onValueChange={(v) => setSelectedUser(prev => ({...prev, role: v}))}
            >
              <SelectTrigger className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="sales_manager">Sales Manager</SelectItem>
                <SelectItem value="sales_rep">Sales Agent</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="px-6 py-4 border-t bg-slate-50/70 gap-2">
            <Button variant="outline" onClick={() => setShowEditUserDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleUpdateUserRole(selectedUser?.id, selectedUser?.role)}
              className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
            >
              <Check size={14} className="mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="reset-password-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-violet-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                <Key size={20} />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-lg font-semibold text-slate-900">Reset Password</DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-0.5">
                  Set a new password for {selectedUser?.full_name || selectedUser?.email}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <form onSubmit={handleResetPassword}>
            <div className="px-6 py-5 space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="new-password" className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength={8}
                    className="h-10 pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password" className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  Confirm Password
                </Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength={8}
                  className="h-10"
                  data-testid="confirm-password-input"
                />
              </div>
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-sm text-red-500">Passwords do not match</p>
              )}
            </div>
            <DialogFooter className="px-6 py-4 border-t bg-slate-50/70 gap-2">
              <Button type="button" variant="outline" onClick={() => setShowResetPasswordDialog(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={resettingPassword || newPassword !== confirmPassword || newPassword.length < 8}
                className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
              >
                {resettingPassword ? (
                  <><RefreshCw size={14} className="mr-2 animate-spin" /> Resetting…</>
                ) : (
                  <><Key size={14} className="mr-2" /> Reset Password</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Effective Permissions Viewer Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="permissions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-500" />
              Effective Permissions
            </DialogTitle>
            <DialogDescription>
              View all permissions for <strong>{selectedUser?.full_name || selectedUser?.email}</strong>.
              These are derived from the user's assigned roles and permission groups.
            </DialogDescription>
          </DialogHeader>
          
          {loadingPermissions ? (
            <div className="py-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
              <p className="mt-3 text-sm text-slate-500">Loading permissions...</p>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              {/* User's Assigned Roles */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                  <Users size={16} className="text-blue-500" />
                  Assigned Roles
                </h4>
                {userRolesForViewer.length === 0 ? (
                  <p className="text-sm text-slate-500">No roles assigned</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {userRolesForViewer.map((role) => (
                      <Badge 
                        key={role.id} 
                        className={`${
                          role.role_code === 'super_admin' ? 'bg-red-500' :
                          role.role_code === 'account_owner' ? 'bg-amber-500' :
                          role.role_code === 'admin' ? 'bg-purple-500' :
                          'bg-blue-500'
                        } text-white`}
                      >
                        {role.role_name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Permission Summary */}
              <div className="text-sm text-slate-600 flex items-center gap-2 px-1">
                <Shield size={14} className="text-purple-500" />
                <span>
                  {Object.keys(effectivePermissions).length} permission(s) across {Object.keys(groupedPermissions).length} module(s)
                </span>
              </div>
              
              {/* Permissions By Module */}
              <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2">
                {Object.keys(groupedPermissions).length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Shield size={40} className="mx-auto mb-3 opacity-30" />
                    <p className="font-medium">No Permissions</p>
                    <p className="text-sm mt-1">This user has no effective permissions. Assign roles to grant access.</p>
                  </div>
                ) : (
                  Object.entries(groupedPermissions).sort(([a], [b]) => a.localeCompare(b)).map(([menu, perms]) => (
                    <div key={menu} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
                        <h4 className="font-medium text-slate-800 text-sm uppercase tracking-wide">
                          {menu.replace(/_/g, ' ')}
                        </h4>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {perms.map(({ code, subMenu, actions }) => (
                          <div key={code} className="px-4 py-2.5 flex items-center justify-between hover:bg-slate-50">
                            <div className="flex items-center gap-2">
                              <Check size={14} className="text-green-500" />
                              <span className="text-sm text-slate-700">{subMenu.replace(/_/g, ' ')}</span>
                            </div>
                            <div className="flex gap-1.5 flex-wrap justify-end">
                              {actions.map((action) => (
                                <Badge 
                                  key={action} 
                                  variant="outline" 
                                  className="text-xs py-0.5 px-2 bg-white"
                                >
                                  {action}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Super Admin Notice */}
              {userRolesForViewer.some(r => r.role_code === 'super_admin' || r.role_code === 'account_owner') && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <Lock size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <strong>Full Access:</strong> This user has a system role (Super Admin or Account Owner) 
                    which grants full access to all features regardless of specific permission assignments.
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPermissionsDialog(false);
                openRoleAssignDialog(selectedUser);
              }}
              className="gap-2"
            >
              <Edit size={14} />
              Edit Roles
            </Button>
            <Button onClick={() => setShowPermissionsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RBAC Role Assignment Dialog */}
      <Dialog open={showRoleAssignDialog} onOpenChange={setShowRoleAssignDialog}>
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-violet-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                <Users size={20} />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-lg font-semibold text-slate-900">Assign RBAC Roles</DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-0.5">
                  Assign roles to <strong className="text-slate-700">{selectedUser?.full_name || selectedUser?.email}</strong>. Roles determine what the user can access.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 py-5">
            {loadingRoles ? (
              <div className="py-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500 mx-auto"></div>
                <p className="mt-2 text-sm text-slate-500">Loading roles…</p>
              </div>
            ) : (
              <div className="space-y-2.5 max-h-[60vh] overflow-y-auto">
                {rbacRoles.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No assignable roles available</p>
                ) : (
                  rbacRoles.map((role) => {
                    const isAssigned = userRbacRoles.some(r => r.id === role.id);
                    return (
                      <div
                        key={role.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          isAssigned
                            ? 'border-violet-500 bg-violet-50'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                        onClick={() => handleToggleUserRole(role.id)}
                        data-testid={`role-option-${role.id}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <h4 className="font-medium text-slate-900 truncate">{role.role_name}</h4>
                            {role.description && (
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{role.description}</p>
                            )}
                            <p className="text-xs text-slate-400 mt-1">
                              Level {role.role_level} • {role.permission_groups?.length || 0} permission groups
                            </p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                            isAssigned ? 'border-violet-500 bg-violet-500' : 'border-slate-300'
                          }`}>
                            {isAssigned && <Check size={12} className="text-white" />}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-slate-50/70 gap-2">
            <Button variant="outline" onClick={() => setShowRoleAssignDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveUserRoles}
              disabled={savingRoles || loadingRoles}
              className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
              data-testid="save-roles-btn"
            >
              {savingRoles ? (
                <><RefreshCw size={14} className="mr-2 animate-spin" /> Saving…</>
              ) : (
                <><Check size={14} className="mr-2" /> Save Roles</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Form Dialog */}
      <Dialog open={showEditUserFormDialog} onOpenChange={setShowEditUserFormDialog}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="edit-user-form-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-violet-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                <Edit size={20} />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-lg font-semibold text-slate-900">Edit User</DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-0.5">
                  Update details for {editingUser?.full_name || editingUser?.email}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
            <div className="space-y-1.5">
              <Label htmlFor="edit-name" className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Display Name
              </Label>
              <Input
                id="edit-name"
                value={editUserForm.full_name}
                onChange={(e) => setEditUserForm({ ...editUserForm, full_name: e.target.value })}
                placeholder="John Doe"
                className="h-10"
                data-testid="edit-user-name"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-phone" className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Mobile Number
              </Label>
              <Input
                id="edit-phone"
                value={editUserForm.phone}
                onChange={(e) => setEditUserForm({ ...editUserForm, phone: e.target.value })}
                placeholder="+971501234567"
                className="h-10"
                data-testid="edit-user-phone"
              />
              <p className="text-[11px] text-slate-400">Used for internal notifications and callbacks</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-organization" className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Organization
              </Label>
              {user?.role === 'super_admin' ? (
                <Select
                  value={editUserForm.organization_id || 'none'}
                  onValueChange={(v) => setEditUserForm({ ...editUserForm, organization_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger className="h-10" data-testid="edit-user-organization">
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Organization</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="edit-organization"
                  value={organizations.find(o => o.id === editUserForm.organization_id)?.name || 'No Organization'}
                  disabled
                  className="h-10 bg-slate-100"
                  data-testid="edit-user-organization"
                />
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-role" className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Role
              </Label>
              <Select
                value={editUserForm.role || 'sales_rep'}
                onValueChange={(v) => setEditUserForm({ ...editUserForm, role: v })}
              >
                <SelectTrigger className="h-10" data-testid="edit-user-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                {/* Pulled from /admin/rbac-roles so any role added by an
                    admin in Roles & Permissions appears here without a
                    code change. The previous hardcoded list was missing
                    the real roles (bdm, ceo, hod_sales, hod_marketing,
                    marketing, cr_agent, ai_agent) — users editing a BDM
                    account got an empty dropdown and couldn't save. */}
                <SelectContent>
                  {availableRoles.length > 0 ? (
                    availableRoles.map((role) => (
                      <SelectItem key={role.id || role.role_code} value={role.role_code}>
                        {role.role_name}
                      </SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="sales_rep">Sales Agent</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* HubSpot Integration — grouped as a soft card so it
                reads as one "external mapping" section, matching the
                When-card treatment on the CEO Assign Task dialog. */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-4">
              <h4 className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-orange-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
                HubSpot Integration
              </h4>

              <div className="space-y-1.5">
                <Label htmlFor="edit-hubspot-user-id" className="text-xs text-slate-600 font-medium">
                  HubSpot User ID
                </Label>
                <Input
                  id="edit-hubspot-user-id"
                  value={editUserForm.hubspot_user_id || ''}
                  onChange={(e) => setEditUserForm({ ...editUserForm, hubspot_user_id: e.target.value })}
                  placeholder="e.g., 12345678"
                  className="h-10 bg-white"
                  data-testid="edit-hubspot-user-id"
                />
                <p className="text-[11px] text-slate-400">Required for syncing lead ownership to HubSpot</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-hubspot-email" className="text-xs text-slate-600 font-medium">
                  HubSpot User Email
                </Label>
                <Input
                  id="edit-hubspot-email"
                  value={editUserForm.hubspot_user_email || ''}
                  onChange={(e) => setEditUserForm({ ...editUserForm, hubspot_user_email: e.target.value })}
                  placeholder="user@company.com"
                  className="h-10 bg-white"
                  data-testid="edit-hubspot-email"
                />
                <p className="text-[11px] text-slate-400">Optional — for reference only</p>
              </div>

              <div className="flex items-start gap-2 pt-1">
                <input
                  type="checkbox"
                  id="edit-hubspot-sync-enabled"
                  checked={editUserForm.hubspot_sync_enabled !== false}
                  onChange={(e) => setEditUserForm({ ...editUserForm, hubspot_sync_enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 accent-violet-600 mt-0.5"
                  data-testid="edit-hubspot-sync-enabled"
                />
                <div>
                  <Label htmlFor="edit-hubspot-sync-enabled" className="text-sm cursor-pointer text-slate-700">
                    Enable HubSpot Owner Sync
                  </Label>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    When enabled, lead ownership changes will sync to HubSpot
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-slate-50/70 gap-2">
            <Button variant="outline" onClick={() => setShowEditUserFormDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEditUser}
              disabled={savingUser}
              className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
            >
              {savingUser ? (
                <><RefreshCw size={14} className="mr-2 animate-spin" /> Saving…</>
              ) : (
                <><Check size={14} className="mr-2" /> Save Changes</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
